import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categories, getMoviesByCategory } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';
import Breadcrumb from '@/components/feature/Breadcrumb';

function MovieCard({ movie }: { movie: Movie }) {
  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[2/3] overflow-hidden">
        <img
          src={movie.poster}
          alt={movie.title}
          title={`${movie.title} - 极速影视高清电影大全`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-2 right-2 bg-primary-500 text-background-50 text-xs font-bold px-2 py-1 rounded-md whitespace-nowrap">
          {movie.rating}
        </div>
        {movie.isNew && (
          <div className="absolute top-2 left-2 bg-accent-500 text-background-50 text-xs font-bold px-2 py-1 rounded-md whitespace-nowrap">
            新上映
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
          <span className="text-background-50 text-sm font-medium whitespace-nowrap">点击查看详情</span>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground-900 truncate mb-1">{movie.title}</h3>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span>{movie.categoryName}</span>
          <span>{movie.year}</span>
        </div>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || 'all';
  const [movies, setMovies] = useState<Movie[]>([]);

  useEffect(() => {
    setMovies(getMoviesByCategory(activeSlug));
  }, [activeSlug]);

  const categoryName = categories.find((c) => c.slug === activeSlug)?.name || '全部';
  const pageTitle = activeSlug === 'all' ? '全部电影' : `${categoryName}电影`;

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="relative w-full py-12 md:py-16 bg-background-100">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: pageTitle },
            ]}
          />
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mt-4" style={{ fontFamily: 'var(--font-heading)' }}>
            {pageTitle}
          </h1>
          <p className="text-sm text-foreground-500 mt-2">
            极速影视为您精选{categoryName}类高清大片，在线免费观看不缓冲，畅享极致观影体验。
          </p>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="w-full px-4 md:px-6 py-6 bg-background-50 border-b border-background-200">
        <div className="flex items-center flex-wrap gap-2">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap ${
                activeSlug === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>
      </section>

      {/* Movie Grid */}
      <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-50">
        {movies.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {movies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <i className="ri-film-line text-5xl text-foreground-300 mb-4 block"></i>
            <p className="text-foreground-500">暂无该分类的电影，请浏览其他分类</p>
            <Link to="/category/all" className="inline-block mt-4 px-6 py-2 bg-primary-500 text-background-50 text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap">
              浏览全部
            </Link>
          </div>
        )}
      </section>

      {/* SEO */}
      <section className="w-full px-4 md:px-6 py-10 bg-background-100">
        <div className="max-w-3xl">
          <h2 className="text-lg font-bold text-foreground-900 mb-3">
            极速影视{categoryName}高清电影大全
          </h2>
          <p className="text-sm text-foreground-600 leading-relaxed">
            极速影视{categoryName}电影频道汇集了最新最全的{categoryName}类高清大片，所有资源免费在线观看不缓冲。极速影视最新院线同步更新，每日不断丰富{categoryName}电影资源库，为您提供极致的{categoryName}观影体验。无论您是{categoryName}电影爱好者还是想要探索{categoryName}类型的观众，极速影视都能满足您的观影需求。
          </p>
        </div>
      </section>

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `极速影视 - ${pageTitle}`,
            "description": `极速影视${pageTitle}，最新最全${categoryName}电影免费在线观看不缓冲。`,
            "url": `https://www.jisumovies.com/category/${activeSlug}`,
            "isPartOf": {
              "@type": "WebSite",
              "name": "极速影视",
              "url": "https://www.jisumovies.com/"
            },
            "inLanguage": "zh-CN",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "首页",
                  "item": "https://www.jisumovies.com/"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": pageTitle
                }
              ]
            }
          }),
        }}
      />
    </div>
  );
}